import React, { useState, useEffect } from 'react';
import { Card, Table, Input, DatePicker, Button, Space, Drawer, Descriptions, message, Image, Row, Col, Modal, Tabs, AutoComplete, Spin } from 'antd';
import { SearchOutlined, DownloadOutlined, PictureOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { accidentService, dictionaryService, authService } from '../../services';
import './AccidentQuery.css';

const AccidentQuery = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [filters, setFilters] = useState({});
    const [tempFilters, setTempFilters] = useState({}); // 临时筛选条件，用于用户输入
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedAccident, setSelectedAccident] = useState(null);
    const [previewUrls, setPreviewUrls] = useState({
        vertical: null,
        horizontal: null,
        scene: null,
        overhead: null
    }); // 4种现场图的临时URL
    const [previewLoading, setPreviewLoading] = useState(false);
    const [investigators, setInvestigators] = useState([]); // 勘查员列表
    const [drawers, setDrawers] = useState([]); // 绘图员列表
    const [currentUser, setCurrentUser] = useState(null); // 当前用户
    const [isDrawerLoading, setDrawerLoading] = useState(false);

    // 现场绘图相关状态
    const [showDrawing, setShowDrawing] = useState(false);
    const [currentAccidentId, setCurrentAccidentId] = useState(null);
    const [hdInitOk, setHdInitOk] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('加载中...');
    const [savedZipData, setSavedZipData] = useState(null);
    const [iframeReady, setIframeReady] = useState(false);
    const iframeContainerRef = React.useRef(null);
    const fileInputRef = React.useRef(null);
    const hdInitCompleteTimeRef = React.useRef(0); // hdInit 完成的时间戳，1000ms 内的 hd:close-hd 是旧 session 清理事件
    const closeDialogRef = React.useRef(false);   // 防止同时弹出多个保存对话框

    useEffect(() => {
        fetchAccidents();
    }, [page, limit, filters]);

    // 获取当前用户信息
    useEffect(() => {
        const loadCurrentUser = async () => {
            try {
                const response = await authService.getCurrentUser();
                if (response.data.code === 200) {
                    setCurrentUser(response.data.data);
                }
            } catch (error) {
                console.error('获取用户信息失败:', error);
            }
        };
        loadCurrentUser();
    }, []);

    // 加载勘查员和绘图员字典
    useEffect(() => {
        if (!currentUser) return;

        const loadDictionaries = async () => {
            try {
                // 加载勘查员
                const investigatorRes = await dictionaryService.getAllDictionaries('investigator', currentUser.user_unit_id);
                if (investigatorRes.data.code === 200) {
                    setInvestigators(investigatorRes.data.data.map(item => ({
                        value: item.name,
                        label: item.name
                    })));
                }

                // 加载绘图员
                const drawerRes = await dictionaryService.getAllDictionaries('drawer', currentUser.user_unit_id);
                if (drawerRes.data.code === 200) {
                    setDrawers(drawerRes.data.data.map(item => ({
                        value: item.name,
                        label: item.name
                    })));
                }
            } catch (error) {
                console.error('加载字典失败:', error);
            }
        };

        loadDictionaries();
    }, [currentUser]);

    const fetchAccidents = async () => {
        try {
            setLoading(true);
            const response = await accidentService.getAccidentList(page, limit, filters);
            if (response.data.code === 200) {
                setData(response.data.data.data);
                setTotal(response.data.data.total);
            }
        } catch (error) {
            message.error('获取事故列表失败');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setPage(1);
        const newFilters = {};
        if (tempFilters.accident_id) newFilters.accident_id = tempFilters.accident_id;
        if (tempFilters.location) newFilters.location = tempFilters.location;
        if (tempFilters.investigator) newFilters.investigator = tempFilters.investigator;
        if (tempFilters.drawer) newFilters.drawer = tempFilters.drawer;
        if (tempFilters.startTime) {
            // 确保startTime是dayjs对象
            newFilters.startTime = dayjs.isDayjs(tempFilters.startTime)
                ? tempFilters.startTime.format('YYYY-MM-DD')
                : dayjs(tempFilters.startTime).format('YYYY-MM-DD');
        }
        if (tempFilters.endTime) {
            // 确保endTime是dayjs对象
            newFilters.endTime = dayjs.isDayjs(tempFilters.endTime)
                ? tempFilters.endTime.format('YYYY-MM-DD')
                : dayjs(tempFilters.endTime).format('YYYY-MM-DD');
        }
        if (tempFilters.drawStart) {
            newFilters.drawStart = dayjs.isDayjs(tempFilters.drawStart)
                ? tempFilters.drawStart.format('YYYY-MM-DD')
                : dayjs(tempFilters.drawStart).format('YYYY-MM-DD');
        }
        if (tempFilters.drawEnd) {
            newFilters.drawEnd = dayjs.isDayjs(tempFilters.drawEnd)
                ? tempFilters.drawEnd.format('YYYY-MM-DD')
                : dayjs(tempFilters.drawEnd).format('YYYY-MM-DD');
        }
        setFilters(newFilters);
    };

    const handleDownload = async (id, type) => {
        try {
            const service = type === 'preview'
                ? accidentService.downloadPreviewImage
                : accidentService.downloadDataPackage;
            const response = await service(id);

            // 从响应头中解析文件名与类型
            const contentType = response.headers['content-type'] || 'application/octet-stream';
            const disposition = response.headers['content-disposition'] || '';
            let filename = `accident-${id}-${type}`;
            // 解析 Content-Disposition 中的文件名
            try {
                const matchStar = disposition.match(/filename\*=(?:UTF-8''|)([^;\n]+)/i);
                const matchBasic = disposition.match(/filename="?([^";\n]+)"?/i);
                if (matchStar && matchStar[1]) {
                    filename = decodeURIComponent(matchStar[1]);
                } else if (matchBasic && matchBasic[1]) {
                    filename = matchBasic[1];
                }
            } catch { }

            // 如果后端未给扩展名，按类型补一个常见扩展
            if (!/\.[a-zA-Z0-9]+$/.test(filename)) {
                const extMap = {
                    'image/jpeg': '.jpg',
                    'image/png': '.png',
                    'image/gif': '.gif',
                    'application/zip': '.zip',
                    'application/x-zip-compressed': '.zip'
                };
                const ext = extMap[contentType] || '';
                filename = `${filename}${ext}`;
            }

            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            message.error('下载失败');
        }
    };

    // 打印图片（根据类型使用不同的页面方向）
    const handlePrintImage = (imageUrl, type) => {
        // 创建一个隐藏的iframe用于打印
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            message.error('无法打开打印窗口，请检查浏览器弹窗设置');
            return;
        }

        // 竖版用portrait，其他用landscape
        const orientation = type === 'vertical' ? 'portrait' : 'landscape';

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>打印图纸</title>
                <meta charset="utf-8">
                <style>
                    @page {
                        size: A4 ${orientation};
                        margin: 0mm;
                    }
                    * {
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                        box-sizing: border-box;
                    }
                    html {
                        margin: 0 !important;
                        padding: 0 !important;
                        width: 100%;
                        height: 100%;
                    }
                    body {
                        margin: 0 !important;
                        padding: 0 !important;
                        width: 100%;
                        height: 100vh;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: white;
                        overflow: hidden;
                    }
                    img {
                        max-width: 100%;
                        max-height: 100vh;
                        width: auto;
                        height: auto;
                        object-fit: contain;
                        display: block;
                        margin: 0;
                        padding: 0;
                    }
                    @media print {
                        @page {
                            margin: 0mm;
                        }
                        html {
                            margin: 0 !important;
                            padding: 0 !important;
                            height: 100%;
                        }
                        body {
                            margin: 0 !important;
                            padding: 0 !important;
                            height: 100%;
                            width: 100%;
                            overflow: hidden;
                            page-break-after: avoid !important;
                            page-break-before: avoid !important;
                        }
                        img {
                            max-width: 100%;
                            max-height: 100%;
                            width: auto;
                            height: auto;
                            margin: 0;
                            padding: 0;
                            page-break-inside: avoid !important;
                            page-break-after: avoid !important;
                            page-break-before: avoid !important;
                        }
                    }
                </style>
                <script>
                    window.onload = function() {
                        // 提示用户在打印对话框中关闭页眉页脚
                        console.log('提示：在打印设置中，请确保"页眉和页脚"选项已关闭');
                    };
                </script>
            </head>
            <body>
                <img src="${imageUrl}" onload="setTimeout(() => { window.print(); setTimeout(() => window.close(), 100); }, 200);" onerror="alert('图片加载失败'); window.close();" alt="打印图纸" />
            </body>
            </html>
        `);
        printWindow.document.close();

        // 显示提示信息
        message.info('提示：如果打印预览中有页眉页脚，请在打印设置中关闭"页眉和页脚"选项', 3);
    };

    // 打开详情时尝试加载所有4种现场图（生成临时URL）
    useEffect(() => {
        const loadPreviews = async () => {
            if (!drawerVisible || !selectedAccident) return;

            setPreviewLoading(true);
            const urls = {};

            try {
                // 1. 加载竖版图
                if (selectedAccident.vertical_image_path) {
                    try {
                        const resp = await accidentService.downloadSceneImage(selectedAccident.id, 'vertical');
                        const contentType = resp.headers['content-type'] || 'image/jpeg';
                        const blob = new Blob([resp.data], { type: contentType });
                        urls.vertical = window.URL.createObjectURL(blob);
                    } catch (e) {
                        console.error('加载竖版图失败:', e);
                    }
                }

                // 2. 加载横版图
                if (selectedAccident.horizontal_image_path) {
                    try {
                        const resp = await accidentService.downloadSceneImage(selectedAccident.id, 'horizontal');
                        const contentType = resp.headers['content-type'] || 'image/jpeg';
                        const blob = new Blob([resp.data], { type: contentType });
                        urls.horizontal = window.URL.createObjectURL(blob);
                    } catch (e) {
                        console.error('加载横版图失败:', e);
                    }
                }

                // 3. 加载实景记录图
                if (selectedAccident.scene_image_path) {
                    try {
                        const resp = await accidentService.downloadSceneImage(selectedAccident.id, 'scene');
                        const contentType = resp.headers['content-type'] || 'image/jpeg';
                        const blob = new Blob([resp.data], { type: contentType });
                        urls.scene = window.URL.createObjectURL(blob);
                    } catch (e) {
                        console.error('加载实景记录图失败:', e);
                    }
                }

                // 4. 加载实景俯瞰记录图
                if (selectedAccident.overhead_image_path) {
                    try {
                        const resp = await accidentService.downloadSceneImage(selectedAccident.id, 'overhead');
                        const contentType = resp.headers['content-type'] || 'image/jpeg';
                        const blob = new Blob([resp.data], { type: contentType });
                        urls.overhead = window.URL.createObjectURL(blob);
                    } catch (e) {
                        console.error('加载实景俯瞰记录图失败:', e);
                    }
                }

                setPreviewUrls(urls);
            } finally {
                setPreviewLoading(false);
            }
        };

        loadPreviews();

        // 清理：抽屉关闭或选中事故变化时释放所有URL
        return () => {
            Object.values(previewUrls).forEach(url => {
                if (url) window.URL.revokeObjectURL(url);
            });
            setPreviewUrls({ vertical: null, horizontal: null, scene: null, overhead: null });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerVisible, selectedAccident]);

    // Modal 打开/关闭后的回调
    const handleModalAfterOpenChange = (open) => {
        if (open) {
            // 禁用宿主页面滚动条，并补偿滚动条宽度以防止抖动
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.documentElement.style.overflow = 'hidden';
            document.body.style.overflow = 'hidden';
            if (scrollbarWidth > 0) {
                document.body.style.paddingRight = `${scrollbarWidth}px`;
            }

            // 动态计算头部高度
            try {
                const modalEl = document.querySelector('.fullscreen-modal .ant-modal');
                const headerEl = modalEl ? modalEl.querySelector('.ant-modal-header') : null;
                const bodyEl = modalEl ? modalEl.querySelector('.ant-modal-body') : null;
                const headerH = headerEl ? headerEl.offsetHeight : 0;
                if (bodyEl) bodyEl.style.height = headerH ? `calc(100vh - ${headerH}px)` : '100vh';
                if (iframeContainerRef.current) iframeContainerRef.current.style.height = headerH ? `calc(100vh - ${headerH}px)` : '100vh';
            } catch { }

            // 每次打开都创建全新 iframe，确保 WASM 从干净状态初始化
            const staleIframe = document.getElementById('hd-frame');
            if (staleIframe) staleIframe.remove();
            if (typeof window.hdResetLoadState === 'function') window.hdResetLoadState();

            const iframe = document.createElement('iframe');
            iframe.id = 'hd-frame';
            iframe.setAttribute('allow', 'geolocation; clipboard-read; clipboard-write; storage-access');
            iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
            iframe.setAttribute('scrolling', 'no');
            iframe.style.cssText = 'width:100%;height:100%;border:none;margin:0;padding:0;display:block;background:#fff;position:absolute;top:0;left:0;overflow:hidden;';

            iframeContainerRef.current.appendChild(iframe);

            setTimeout(() => {
                if (typeof window.hdLoadWasm === 'function') {
                    window.hdLoadWasm();
                    setIframeReady(true);
                }
            }, 100);
        } else if (!open) {
            // Modal 关闭：移除 iframe，彻底释放 WASM 状态，下次打开走全新初始化
            const existingIframe = document.getElementById('hd-frame');
            if (existingIframe) {
                if (typeof window.hdResetLoadState === 'function') window.hdResetLoadState();
                existingIframe.remove();
            }
            setIframeReady(false);
            setHdInitOk(false);

            // 恢复页面滚动和padding
            document.documentElement.style.overflow = '';
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        }
    };

    // ========== 提供给 WASM 的“导入图片”桥接与按钮逻辑 ==========
    const injectImageBytes = async (u8) => {
        if (!hdInitOk || !currentAccidentId) {
            message.warning('绘图未就绪，稍后再试');
            return { success: false };
        }
        if (typeof window.hdAppendSceneImage !== 'function') {
            message.error('当前版本不支持导入照片');
            return { success: false };
        }
        try {
            const res = await window.hdAppendSceneImage(String(currentAccidentId), u8);
            if (res?.success) {
                message.success('已导入一张照片');
                return { success: true };
            }
            message.error('导入失败');
            return { success: false };
        } catch (e) {
            console.error('injectImageBytes failed:', e);
            message.error('导入异常: ' + e.message);
            return { success: false };
        }
    };

    const injectImageFromFile = async (file) => {
        if (!file) return;
        if (!/^image\//i.test(file.type)) {
            message.warning('仅支持图片文件');
            return;
        }
        if (file.size > 15 * 1024 * 1024) {
            message.warning('图片过大，请选择小于15MB的图片');
            return;
        }
        const buf = await file.arrayBuffer();
        const u8 = new Uint8Array(buf);
        await injectImageBytes(u8);
    };

    const handleImportPhotoClick = () => {
        if (!hdInitOk) {
            message.info('WASM 正在初始化，请稍候');
            return;
        }
        if (fileInputRef.current) {
            try { fileInputRef.current.value = ''; } catch { }
            fileInputRef.current.click();
        }
    };

    const handleFileInputChange = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (file) {
            await injectImageFromFile(file);
        }
        try { e.target.value = ''; } catch { }
    };

    const handleImportTest = async () => {
        if (!hdInitOk) {
            message.info('WASM 正在初始化，请稍候');
            return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = 1024;
        canvas.height = 768;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#f5f5f5';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#1890ff';
        ctx.fillRect(40, 40, canvas.width - 80, canvas.height - 80);
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 48px system-ui, -apple-system, Segoe UI, Roboto, Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('导入测试照片', canvas.width / 2, canvas.height / 2 - 30);
        ctx.font = 'normal 28px system-ui, -apple-system, Segoe UI, Roboto, Arial';
        ctx.fillText(dayjs().format('YYYY-MM-DD HH:mm:ss'), canvas.width / 2, canvas.height / 2 + 25);
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));
        const buf = await blob.arrayBuffer();
        const u8 = new Uint8Array(buf);
        await injectImageBytes(u8);
    };

    useEffect(() => {
        if (!showDrawing) return;
        const onMessage = async (ev) => {
            const data = ev?.data;
            if (!data || typeof data !== 'object') return;
            if (data.type === 'hd:import-scene-image') {
                handleImportPhotoClick();
            } else if (data.type === 'hd:import-scene-image-url' && data.url) {
                try {
                    const resp = await fetch(data.url, { mode: 'cors' });
                    const blob = await resp.blob();
                    const buf = await blob.arrayBuffer();
                    const u8 = new Uint8Array(buf);
                    await injectImageBytes(u8);
                } catch (e) {
                    message.error('从URL导入失败');
                }
            } else if (data.type === 'hd:import-scene-image-bytes' && data.bytes_b64) {
                try {
                    const bin = atob(data.bytes_b64);
                    const u8 = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
                    await injectImageBytes(u8);
                } catch (e) {
                    message.error('导入字节流失败');
                }
            } else if (data.type === 'hd:close-hd') {
                // hdInit 完成后 1000ms 内的事件是 WASM 关闭旧 session 的回调，忽略
                if (hdInitOk && (Date.now() - hdInitCompleteTimeRef.current > 1000))
                    handleCloseDrawing({ reason: data.reason || '' });
            }
        };
        window.addEventListener('message', onMessage);
        window.hdHostSelectAndAppendSceneImage = handleImportPhotoClick;
        window.hdHostAppendSceneImageTest = handleImportTest;
        return () => {
            window.removeEventListener('message', onMessage);
            if (window.hdHostSelectAndAppendSceneImage) delete window.hdHostSelectAndAppendSceneImage;
            if (window.hdHostAppendSceneImageTest) delete window.hdHostAppendSceneImageTest;
        };
    }, [showDrawing, hdInitOk]);

    // 监听 WASM ready 并初始化
    useEffect(() => {
        if (!iframeReady || !currentAccidentId) return;

        let mounted = true;
        setIsLoading(false);
        const waitAndInit = async () => {
            try {
                setIsLoading(false);
                setLoadingText('等待现场绘图就绪...');

                if (typeof window.hdWhenNativeReady === 'function') {
                    await window.hdWhenNativeReady();
                }

                if (!mounted) return;

                setLoadingText('正在初始化...');

                const res = await window.hdInit(String(currentAccidentId));

                if (!mounted) return;

                if (!res || res.rc !== 0) {
                    message.error('初始化失败');
                    setIsLoading(false);
                    return;
                }

                // 如果有保存的zip数据，导入
                if (savedZipData && typeof window.hdImportPackageZip === 'function') {
                    setLoadingText('加载绘图数据...');
                    try {
                        const bin = atob(savedZipData);
                        const u8 = new Uint8Array(bin.length);
                        for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);

                        await window.hdImportPackageZip(String(currentAccidentId), u8);
                    } catch (e) {
                        console.error('导入zip失败:', e);
                    }
                }

                // 始终将详情中的字段推送到 WASM，保持与立案页一致的“打开覆盖/关闭回填”逻辑
                try {
                    setLoadingText('同步数据...');
                    const push = async (key, value) => {
                        if (!value || typeof window.hdSetInfo !== 'function') return;
                        const r = await window.hdSetInfo(String(currentAccidentId), key, String(value));
                        if (!r || r.rc !== 0) console.warn('hdSetInfo 失败', key, r);
                    };

                    const acc = selectedAccident || {};
                    await push(
                        'accidentTime',
                        acc.arrive_time
                            ? (dayjs.isDayjs(acc.arrive_time)
                                ? acc.arrive_time.format('YYYY-MM-DDTHH:mm:ss')
                                : dayjs(acc.arrive_time).format('YYYY-MM-DDTHH:mm:ss'))
                            : null
                    );
                    await push('accidentLocation', acc.location);
                    await push('weather', acc.weather);
                    await push('roadSurfaceType', acc.road_surface);
                    await push('investigatorName', acc.investigator);
                    await push('drafterName', acc.drawer);
                    await push(
                        'finalizedAt',
                        acc.draw_time
                            ? (dayjs.isDayjs(acc.draw_time)
                                ? acc.draw_time.format('YYYY-MM-DDTHH:mm:ss')
                                : dayjs(acc.draw_time).format('YYYY-MM-DDTHH:mm:ss'))
                            : null
                    );
                    await push('remarks', acc.explan);
                } catch (e) {
                    console.warn('同步详情字段到 WASM 失败（忽略并继续）:', e);
                }

                if (!mounted) return;

                setHdInitOk(true);
                hdInitCompleteTimeRef.current = Date.now(); // 记录 hdInit 完成时间，用于过滤旧 session 的 hd:close-hd 事件

                // 调用 WASM 显示导入图片按钮
                if (typeof window.hdSetShowImportImageButton === 'function') {
                    try {
                        await window.hdSetShowImportImageButton(String(currentAccidentId), true);
                        console.log('[AccidentQuery] hdSetShowImportImageButton called');
                    } catch (e) {
                        console.warn('[AccidentQuery] hdSetShowImportImageButton failed:', e);
                    }
                }
                // 调用 WASM 显示标题栏
                if (typeof window.hdSetShowTitleBar === 'function') {
                    try {
                        await window.hdSetShowTitleBar(String(currentAccidentId), true);
                        console.log('[AccidentQuery] hdSetShowTitleBar called');
                    } catch (e) {
                        console.warn('[AccidentQuery] hdSetShowTitleBar failed:', e);
                    }
                }

                setIsLoading(false);
                // 不弹出“现场绘图已就绪”提示
            } catch (e) {
                console.error('初始化失败:', e);
                if (mounted) {
                    setIsLoading(false);
                    message.error('初始化异常: ' + e.message);
                }
            }
        };

        waitAndInit();

        return () => { mounted = false; };
    }, [iframeReady, currentAccidentId, savedZipData]);

    // 打开现场绘图
    const handleOpenDrawing = async () => {
        if (!selectedAccident) {
            message.warning('请先选择事故');
            return;
        }

        setDrawerLoading(true);
        try {
            // 如果有数据包，先加载
            if (selectedAccident.data_package_path) {
                const resp = await accidentService.downloadDataPackage(selectedAccident.id);
                const blob = new Blob([resp.data]);

                const base64Data = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(String(reader.result).split(',')[1]);
                    reader.onerror = (error) => reject(error);
                    reader.readAsDataURL(blob);
                });

                setSavedZipData(base64Data);
            }

            // setDrawerVisible(false); // 关闭详情抽屉，防止抖动
            // Continue to start wasm
            closeDialogRef.current = false; // 新会话开始，重置对话框状态
            setCurrentAccidentId(selectedAccident.accident_id);
            setShowDrawing(true);
            setHdInitOk(false);
            setIsLoading(true);
            setLoadingText('加载 WASM...');
        } catch (e) {
            console.error('加载数据包失败:', e);
            message.error('加载数据包失败!');
        } finally {
            setDrawerLoading(false);
        }
    };

    // 关闭绘图工具
    const handleCloseDrawing = async ({ reason = '' } = {}) => {
        const isForceClose = reason === 'fatal_error';
        if (closeDialogRef.current) return; // 已有对话框在显示，防止重复弹出
        if (!hdInitOk || !currentAccidentId) {
            setShowDrawing(false);
            return;
        }
        closeDialogRef.current = true;

        // 保存逻辑函数
        const saveDrawing = async () => {
            try {
                setIsLoading(true);
                setLoadingText('正在保存...');

                // 1. 先从 WASM 获取最新字段信息并同步到 selectedAccident
                let latestAccidentForSave = selectedAccident;
                try {
                    setLoadingText('同步字段信息...');

                    // hdGetInfo 需要逐个字段调用
                    const wasmInfo = {};

                    // 获取各个字段
                    const fields = [
                        { wasmKey: 'accidentTime', formKey: 'arrive_time' },
                        { wasmKey: 'accidentLocation', formKey: 'location' },
                        { wasmKey: 'weather', formKey: 'weather' },
                        { wasmKey: 'roadSurfaceType', formKey: 'road_surface' },
                        { wasmKey: 'investigatorName', formKey: 'investigator' },
                        { wasmKey: 'drafterName', formKey: 'drawer' },
                        { wasmKey: 'finalizedAt', formKey: 'draw_time' },
                        { wasmKey: 'remarks', formKey: 'explan' }
                    ];

                    for (const field of fields) {
                        try {
                            const res = await window.hdGetInfo(String(currentAccidentId), field.wasmKey);
                            if (res?.success && res.value) {
                                wasmInfo[field.formKey] = res.value;
                            }
                        } catch (e) {
                            // 单个字段失败不影响整体
                        }
                    }

                    // 更新 selectedAccident 状态（一次性合并，精简处理）
                    // 注意：保留后端原始的创建者字段，不被 WASM 覆盖
                    if (Object.keys(wasmInfo).length > 0) {
                        latestAccidentForSave = {
                            ...selectedAccident,
                            ...wasmInfo,
                            creator_name: selectedAccident.creator_name,
                            created_by: selectedAccident.created_by,
                            id: selectedAccident.id
                        };
                        // arrive_time 字段同步
                        if (wasmInfo.arrive_time) {
                            latestAccidentForSave.arrive_time = dayjs(wasmInfo.arrive_time);
                        }
                        setSelectedAccident(latestAccidentForSave);
                        // 不弹出“字段信息已同步”提示
                    } else {
                        message.info('没有可同步的字段');
                    }
                } catch (e) {
                    console.error('[AccidentQuery] 获取WASM字段信息失败:', e);
                    message.warning('同步字段信息失败: ' + e.message);
                }

                // 导出所有类型的现场图
                const imageFiles = {};

                // 1. 竖版 (type=0, exportFormat=0)
                setLoadingText('导出竖版图纸...');
                const verticalRes = await window.hdGetAccidentSceneDiagram(String(currentAccidentId), 0, 0);
                if (verticalRes?.success && verticalRes.value_b64) {
                    const bin = atob(verticalRes.value_b64);
                    const u8 = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
                    const blob = new Blob([u8], { type: verticalRes.mime || 'image/jpeg' });
                    imageFiles.vertical = new File([blob], `现场图-竖版-${currentAccidentId}.jpg`, { type: verticalRes.mime || 'image/jpeg' });
                    imageFiles.vertical = new File([blob], `vertical-${currentAccidentId}.jpg`, { type: verticalRes.mime || 'image/jpeg' });
                }

                // 2. 横版 (type=1, exportFormat=0)
                setLoadingText('导出横版图纸...');
                const horizontalRes = await window.hdGetAccidentSceneDiagram(String(currentAccidentId), 1, 0);
                if (horizontalRes?.success && horizontalRes.value_b64) {
                    const bin = atob(horizontalRes.value_b64);
                    const u8 = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
                    const blob = new Blob([u8], { type: horizontalRes.mime || 'image/jpeg' });
                    imageFiles.horizontal = new File([blob], `现场图-横版-${currentAccidentId}.jpg`, { type: horizontalRes.mime || 'image/jpeg' });
                    imageFiles.horizontal = new File([blob], `horizontal-${currentAccidentId}.jpg`, { type: horizontalRes.mime || 'image/jpeg' });
                }

                // 横版作为默认预览图（保持兼容性）
                imageFiles.preview = imageFiles.horizontal;

                // 3. 实景记录图 (type=2, exportFormat=0)
                setLoadingText('导出实景记录图...');
                const sceneRes = await window.hdGetAccidentSceneDiagram(String(currentAccidentId), 2, 0);
                if (sceneRes?.success && sceneRes.value_b64) {
                    const bin = atob(sceneRes.value_b64);
                    const u8 = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
                    const blob = new Blob([u8], { type: sceneRes.mime || 'image/jpeg' });
                    imageFiles.scene = new File([blob], `现场图-实景记录图-${currentAccidentId}.jpg`, { type: sceneRes.mime || 'image/jpeg' });
                    imageFiles.scene = new File([blob], `scene-${currentAccidentId}.jpg`, { type: sceneRes.mime || 'image/jpeg' });
                }

                // 4. 实景俯瞰记录图 (type=3, exportFormat=0)
                setLoadingText('导出实景俯瞰记录图...');
                const overheadRes = await window.hdGetAccidentSceneDiagram(String(currentAccidentId), 3, 0);
                if (overheadRes?.success && overheadRes.value_b64) {
                    const bin = atob(overheadRes.value_b64);
                    const u8 = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
                    const blob = new Blob([u8], { type: overheadRes.mime || 'image/jpeg' });
                    imageFiles.overhead = new File([blob], `现场图-实景俯瞰记录图-${currentAccidentId}.jpg`, { type: overheadRes.mime || 'image/jpeg' });
                    imageFiles.overhead = new File([blob], `overhead-${currentAccidentId}.jpg`, { type: overheadRes.mime || 'image/jpeg' });
                }

                // 导出zip包
                setLoadingText('导出数据包...');
                const zipRes = await window.hdExportPackageZip(String(currentAccidentId));
                let packageFile = null;
                if (zipRes?.success && zipRes.value_b64) {
                    setSavedZipData(zipRes.value_b64);
                    const bin = atob(zipRes.value_b64);
                    const u8 = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
                    const blob = new Blob([u8], { type: zipRes.mime || 'application/zip' });
                    packageFile = new File([blob], `dataPackage-${currentAccidentId}.zip`, { type: zipRes.mime || 'application/zip' });
                }

                // 更新事故
                setLoadingText('更新事故记录...');
                const formData = new FormData();
                // 使用最新的合并值（若上面没有任何字段可同步，则回退到当前 selectedAccident）
                const latest = latestAccidentForSave || selectedAccident;
                formData.append('accident_id', latest.accident_id);
                formData.append('accident_time', dayjs(latest.accident_time).format('YYYY-MM-DD HH:mm:ss'));
                formData.append('arrive_time', latest.arrive_time ? dayjs(latest.arrive_time).format('YYYY-MM-DD HH:mm:ss') : '');
                formData.append('location', latest.location);
                formData.append('weather', latest.weather || '');
                formData.append('road_surface', latest.road_surface || '');
                formData.append('investigator', latest.investigator || '');
                formData.append('drawer', latest.drawer || '');
                formData.append('draw_time', latest.draw_time ? dayjs(latest.draw_time).format('YYYY-MM-DD HH:mm:ss') : '');
                formData.append('explan', latest.explan || '');
                // 上传所有图片
                if (imageFiles.preview) formData.append('previewImage', imageFiles.preview);
                if (imageFiles.vertical) formData.append('verticalImage', imageFiles.vertical);
                if (imageFiles.horizontal) formData.append('horizontalImage', imageFiles.horizontal);
                if (imageFiles.scene) formData.append('sceneImage', imageFiles.scene);
                if (imageFiles.overhead) formData.append('overheadImage', imageFiles.overhead);
                if (packageFile) formData.append('dataPackage', packageFile);

                await accidentService.createOrUpdateAccident(formData);

                message.success('保存成功');
                setSavedZipData(null);
                if (typeof window.hdRequestRelease === 'function') {
                    try { await window.hdRequestRelease(1000); } catch (e) { console.warn('[AccidentQuery] hdRequestRelease failed:', e); }
                }
                setShowDrawing(false);

                // 刷新列表
                await fetchAccidents();

                // 重新从后端获取完整的事故详情（包含最新的图片路径）
                try {
                    const detailRes = await accidentService.getAccidentDetail(selectedAccident.id);
                    if (detailRes.data.code === 200) {
                        const updatedAccident = detailRes.data.data;

                        // 先释放旧预览URLs
                        Object.values(previewUrls).forEach(url => {
                            if (url) window.URL.revokeObjectURL(url);
                        });
                        setPreviewUrls({ vertical: null, horizontal: null, scene: null, overhead: null });

                        // 更新selectedAccident（这会触发useEffect重新加载预览图）
                        setSelectedAccident(updatedAccident);

                        // 确保Drawer保持打开状态
                        if (!drawerVisible) {
                            setDrawerVisible(true);
                        }
                    }
                } catch (e) {
                    console.error('获取事故详情失败:', e);
                    message.error('刷新事故详情失败');
                }
            } catch (e) {
                console.error('保存失败:', e);
                message.error('保存失败: ' + e.message);
            } finally {
                setIsLoading(false);
            }
        };

        // 弹出确认对话框 - 正常操作三个按钮：保存、不保存、取消；会话失效时隐藏取消
        const modal = Modal.warning({
            title: isForceClose ? '会话已过期' : '保存现场绘图',
            content: isForceClose ? '绘图会话已断开，请选择是否保存当前数据。' : '是否保存当前的现场绘图数据？',
            icon: null,
            footer: (_, { OkBtn }) => (
                <>
                    {!isForceClose && (
                        <Button onClick={() => { closeDialogRef.current = false; modal.destroy(); }}>取消</Button>
                    )}
                    <Button danger onClick={async () => {
                        modal.destroy();
                        closeDialogRef.current = false;
                        if (typeof window.hdRequestRelease === 'function') {
                            try { await window.hdRequestRelease(1000); } catch (e) { console.warn('[AccidentQuery] hdRequestRelease failed:', e); }
                        }
                        setShowDrawing(false);
                        message.info('已放弃保存');
                    }}>
                        不保存
                    </Button>
                    <Button type="primary" onClick={() => {
                        modal.destroy();
                        // closeDialogRef 在 saveDrawing 完成后自然失效（modal已关闭）
                        saveDrawing();
                    }}>
                        保存
                    </Button>
                </>
            ),
        });
    };

    const columns = [
        {
            title: '事故ID',
            dataIndex: 'accident_id',
            key: 'accident_id',
            width: 120
        },
        {
            title: '事故发生时间',
            dataIndex: 'accident_time',
            key: 'accident_time',
            width: 180,
            render: (text) => dayjs(text).format('YYYY年MM月DD日 HH时mm分')
        },
        {
            title: '地点',
            dataIndex: 'location',
            key: 'location',
            width: 150
        },
        {
            title: '勘查员',
            dataIndex: 'investigator',
            key: 'investigator',
            width: 100
        },
        {
            title: '绘图员',
            dataIndex: 'drawer',
            key: 'drawer',
            width: 100
        },
        {
            title: '绘图时间',
            dataIndex: 'draw_time',
            key: 'draw_time',
            width: 180,
            render: (text) => text ? dayjs(text).format('YYYY年MM月DD日 HH时mm分') : '—'
        },
        {
            title: '创建人',
            dataIndex: 'creator_name',
            key: 'creator_name',
            width: 120,
            render: (text, record) => text || record.created_by || '未知'
        },
        {
            title: '操作',
            key: 'action',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => {
                            setSelectedAccident(record);
                            setDrawerVisible(true);
                        }}
                    >
                        详情
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <div className="accident-query">
            <Card title="事故查询" className="query-card">
                <Space style={{ marginBottom: 16, width: '100%' }} wrap>
                    <Input
                        placeholder="事故ID"
                        style={{ width: 150 }}
                        onChange={(e) => setTempFilters({ ...tempFilters, accident_id: e.target.value })}
                    />
                    <DatePicker
                        placeholder="事故发生开始日期"
                        style={{ width: 160 }}
                        format="YYYY年MM月DD日"
                        onChange={(date) => {
                            setTempFilters({ ...tempFilters, startTime: date || undefined });
                        }}
                    />
                    <DatePicker
                        placeholder="事故发生结束日期"
                        style={{ width: 160 }}
                        format="YYYY年MM月DD日"
                        onChange={(date) => {
                            setTempFilters({ ...tempFilters, endTime: date || undefined });
                        }}
                    />
                    <Input
                        placeholder="地点"
                        style={{ width: 150 }}
                        onChange={(e) => setTempFilters({ ...tempFilters, location: e.target.value })}
                    />
                    <AutoComplete
                        options={investigators}
                        placeholder="勘查员"
                        style={{ width: 150 }}
                        onChange={(value) => setTempFilters({ ...tempFilters, investigator: value })}
                        filterOption={(inputValue, option) =>
                            option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                        }
                    />
                    <AutoComplete
                        options={drawers}
                        placeholder="绘图员"
                        style={{ width: 150 }}
                        onChange={(value) => setTempFilters({ ...tempFilters, drawer: value })}
                        filterOption={(inputValue, option) =>
                            option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                        }
                    />
                    <DatePicker
                        placeholder="绘图开始日期"
                        format="YYYY年MM月DD日"
                        onChange={(date) => {
                            setTempFilters({ ...tempFilters, drawStart: date || undefined });
                        }}
                    />
                    <DatePicker
                        placeholder="绘图结束日期"
                        format="YYYY年MM月DD日"
                        onChange={(date) => {
                            setTempFilters({ ...tempFilters, drawEnd: date || undefined });
                        }}
                    />
                    <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={handleSearch}
                    >
                        查询
                    </Button>
                </Space>

                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: page,
                        pageSize: limit,
                        total,
                        onChange: (p) => setPage(p),
                        onShowSizeChange: (_, s) => setLimit(s)
                    }}
                />
            </Card>

            <Drawer
                title="事故详情"
                onClose={() => {
                    if (isDrawerLoading) return;
                    setDrawerVisible(false);
                }}
                open={drawerVisible}
                width={800}
                // getContainer={false}
                maskClosable={!isDrawerLoading}
                closable={!isDrawerLoading}
            >
                <Spin spinning={isDrawerLoading} tip="正在准备绘图数据...">
                    {selectedAccident && (
                        <>
                            <Descriptions column={2} bordered>
                                <Descriptions.Item label="事故ID" span={2}>
                                    {selectedAccident.accident_id}
                                </Descriptions.Item>
                                <Descriptions.Item label="事故发生时间" span={2}>
                                    {dayjs(selectedAccident.accident_time).format('YYYY年MM月DD日 HH时mm分')}
                                </Descriptions.Item>
                                <Descriptions.Item label="到达事故现场时间" span={2}>
                                    {selectedAccident.arrive_time ? dayjs(selectedAccident.arrive_time).format('YYYY年MM月DD日 HH时mm分') : '无'}
                                </Descriptions.Item>
                                <Descriptions.Item label="地点" span={2}>
                                    {selectedAccident.location}
                                </Descriptions.Item>
                                <Descriptions.Item label="天气">
                                    {selectedAccident.weather || '无'}
                                </Descriptions.Item>
                                <Descriptions.Item label="路面性质">
                                    {selectedAccident.road_surface || '无'}
                                </Descriptions.Item>
                                <Descriptions.Item label="勘查员">
                                    {selectedAccident.investigator || '无'}
                                </Descriptions.Item>
                                <Descriptions.Item label="绘图员">
                                    {selectedAccident.drawer || '无'}
                                </Descriptions.Item>
                                <Descriptions.Item label="绘图时间">
                                    {selectedAccident.draw_time ? dayjs(selectedAccident.draw_time).format('YYYY年MM月DD日 HH时mm分') : '无'}
                                </Descriptions.Item>
                                <Descriptions.Item label="创建者">
                                    {selectedAccident.creator_name || selectedAccident.created_by || '无'}
                                </Descriptions.Item>
                            </Descriptions>
                            {/* 事故说明单独一行，跨全部列，视觉分隔更明显 */}
                            <Descriptions column={1} bordered style={{ marginTop: 8 }}>
                                <Descriptions.Item span={1} style={{ background: '#fafafa', padding: 0 }}>
                                    <div style={{ fontWeight: 400, fontSize: 14, color: 'rgba(0,0,0,0.85)', marginBottom: 4 }}>事故说明：</div>
                                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', minHeight: 32, width: '100%', fontSize: 14, background: '#fff', borderRadius: 4, padding: '8px 12px' }}>
                                        {selectedAccident.explan ? selectedAccident.explan : '无'}
                                    </div>
                                </Descriptions.Item>
                            </Descriptions>

                            <Row gutter={16} style={{ marginTop: 24 }}>
                                <Col xs={24}>
                                    <div style={{ fontWeight: 400, fontSize: 14, color: 'rgba(0,0,0,0.85)', marginBottom: 8 }}>现场图：</div>
                                    {previewLoading ? (
                                        <div style={{ textAlign: 'center', padding: '40px', color: '#1890ff', fontSize: 16 }}>
                                            <span>现场图加载中，请稍候...</span>
                                        </div>
                                    ) : (
                                        (previewUrls.vertical || previewUrls.horizontal || previewUrls.scene || previewUrls.overhead) ? (
                                            <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, padding: 16, background: '#fafafa', minHeight: '600px' }}>
                                                <Tabs
                                                    animated={false}
                                                    destroyInactiveTabPane={false}
                                                    defaultActiveKey="horizontal"
                                                    tabBarExtraContent={
                                                        <Button
                                                            type="primary"
                                                            icon={<PictureOutlined />}
                                                            onClick={handleOpenDrawing}
                                                            size="small"
                                                        >
                                                            绘制现场图
                                                        </Button>
                                                    }
                                                    items={[
                                                        {
                                                            key: 'vertical',
                                                            label: '竖版图纸',
                                                            children: previewUrls.vertical ? (
                                                                <div style={{ textAlign: 'center' }}>
                                                                    <Image
                                                                        src={previewUrls.vertical}
                                                                        style={{ maxWidth: '100%' }}
                                                                        placeholder={true}
                                                                        preview={{
                                                                            src: previewUrls.vertical,
                                                                            toolbarRender: (
                                                                                _,
                                                                                {
                                                                                    transform: { scale },
                                                                                    actions: { onFlipY, onFlipX, onRotateLeft, onRotateRight, onZoomOut, onZoomIn, onReset },
                                                                                }
                                                                            ) => (
                                                                                <Space size={12} className="toolbar-wrapper">
                                                                                    <Button
                                                                                        type="primary"
                                                                                        icon={<PrinterOutlined />}
                                                                                        size="middle"
                                                                                        onClick={() => handlePrintImage(previewUrls.vertical, 'vertical')}
                                                                                        style={{
                                                                                            fontSize: '14px',
                                                                                            height: '32px',
                                                                                            borderRadius: '6px',
                                                                                            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                                                                                        }}
                                                                                    >
                                                                                        打印
                                                                                    </Button>
                                                                                    <Button
                                                                                        type="default"
                                                                                        icon={<DownloadOutlined />}
                                                                                        size="middle"
                                                                                        style={{
                                                                                            fontSize: '14px',
                                                                                            height: '32px',
                                                                                            borderRadius: '6px',
                                                                                            borderColor: '#52c41a',
                                                                                            color: '#52c41a',
                                                                                            boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)'
                                                                                        }}
                                                                                        onClick={async () => {
                                                                                            try {
                                                                                                const resp = await accidentService.downloadSceneImage(selectedAccident.id, 'vertical');
                                                                                                const contentType = resp.headers['content-type'] || 'image/jpeg';
                                                                                                const disposition = resp.headers['content-disposition'] || '';
                                                                                                let filename = `竖版图纸-${selectedAccident.accident_id}.jpg`;
                                                                                                try {
                                                                                                    const matchStar = disposition.match(/filename\*=(?:UTF-8''|)([^;\n]+)/i);
                                                                                                    const matchBasic = disposition.match(/filename="?([^";\n]+)"?/i);
                                                                                                    if (matchStar && matchStar[1]) {
                                                                                                        filename = decodeURIComponent(matchStar[1]);
                                                                                                    } else if (matchBasic && matchBasic[1]) {
                                                                                                        filename = matchBasic[1];
                                                                                                    }
                                                                                                } catch { }
                                                                                                const blob = new Blob([resp.data], { type: contentType });
                                                                                                const url = window.URL.createObjectURL(blob);
                                                                                                const link = document.createElement('a');
                                                                                                link.href = url;
                                                                                                link.download = filename;
                                                                                                document.body.appendChild(link);
                                                                                                link.click();
                                                                                                document.body.removeChild(link);
                                                                                                window.URL.revokeObjectURL(url);
                                                                                            } catch (error) {
                                                                                                message.error('下载失败');
                                                                                            }
                                                                                        }} >
                                                                                        下载
                                                                                    </Button>
                                                                                </Space>
                                                                            ),
                                                                        }}
                                                                    />
                                                                </div>
                                                            ) : <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>暂无竖版图纸</div>
                                                        },
                                                        {
                                                            key: 'horizontal',
                                                            label: '横版图纸',
                                                            children: previewUrls.horizontal ? (
                                                                <div style={{ textAlign: 'center' }}>
                                                                    <Image
                                                                        src={previewUrls.horizontal}
                                                                        style={{ maxWidth: '100%' }}
                                                                        placeholder={true}
                                                                        preview={{
                                                                            src: previewUrls.horizontal,
                                                                            toolbarRender: (
                                                                                _,
                                                                                {
                                                                                    transform: { scale },
                                                                                    actions: { onFlipY, onFlipX, onRotateLeft, onRotateRight, onZoomOut, onZoomIn, onReset },
                                                                                }
                                                                            ) => (
                                                                                <Space size={12} className="toolbar-wrapper">
                                                                                    <Button
                                                                                        type="primary"
                                                                                        icon={<PrinterOutlined />}
                                                                                        size="middle"
                                                                                        onClick={() => handlePrintImage(previewUrls.horizontal, 'horizontal')}
                                                                                        style={{
                                                                                            fontSize: '14px',
                                                                                            height: '32px',
                                                                                            borderRadius: '6px',
                                                                                            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                                                                                        }}
                                                                                    >
                                                                                        打印
                                                                                    </Button>
                                                                                    <Button
                                                                                        type="default"
                                                                                        icon={<DownloadOutlined />}
                                                                                        size="middle"
                                                                                        style={{
                                                                                            fontSize: '14px',
                                                                                            height: '32px',
                                                                                            borderRadius: '6px',
                                                                                            borderColor: '#52c41a',
                                                                                            color: '#52c41a',
                                                                                            boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)'
                                                                                        }}
                                                                                        onClick={async () => {
                                                                                            try {
                                                                                                const resp = await accidentService.downloadSceneImage(selectedAccident.id, 'horizontal');
                                                                                                const contentType = resp.headers['content-type'] || 'image/jpeg';
                                                                                                const disposition = resp.headers['content-disposition'] || '';
                                                                                                let filename = `横版图纸-${selectedAccident.accident_id}.jpg`;
                                                                                                try {
                                                                                                    const matchStar = disposition.match(/filename\*=(?:UTF-8''|)([^;\n]+)/i);
                                                                                                    const matchBasic = disposition.match(/filename="?([^";\n]+)"?/i);
                                                                                                    if (matchStar && matchStar[1]) {
                                                                                                        filename = decodeURIComponent(matchStar[1]);
                                                                                                    } else if (matchBasic && matchBasic[1]) {
                                                                                                        filename = matchBasic[1];
                                                                                                    }
                                                                                                } catch { }
                                                                                                const blob = new Blob([resp.data], { type: contentType });
                                                                                                const url = window.URL.createObjectURL(blob);
                                                                                                const link = document.createElement('a');
                                                                                                link.href = url;
                                                                                                link.download = filename;
                                                                                                document.body.appendChild(link);
                                                                                                link.click();
                                                                                                document.body.removeChild(link);
                                                                                                window.URL.revokeObjectURL(url);
                                                                                            } catch (error) {
                                                                                                message.error('下载失败');
                                                                                            }
                                                                                        }} >
                                                                                        下载
                                                                                    </Button>
                                                                                </Space>
                                                                            ),
                                                                        }}
                                                                    />
                                                                </div>
                                                            ) : <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>暂无横版图纸</div>
                                                        },
                                                        {
                                                            key: 'scene',
                                                            label: '实景记录图',
                                                            children: previewUrls.scene ? (
                                                                <div style={{ textAlign: 'center' }}>
                                                                    <Image
                                                                        src={previewUrls.scene}
                                                                        style={{ maxWidth: '100%' }}
                                                                        placeholder={true}
                                                                        preview={{
                                                                            src: previewUrls.scene,
                                                                            toolbarRender: (
                                                                                _,
                                                                                {
                                                                                    transform: { scale },
                                                                                    actions: { onFlipY, onFlipX, onRotateLeft, onRotateRight, onZoomOut, onZoomIn, onReset },
                                                                                }
                                                                            ) => (
                                                                                <Space size={12} className="toolbar-wrapper">
                                                                                    <Button
                                                                                        type="primary"
                                                                                        icon={<PrinterOutlined />}
                                                                                        size="middle"
                                                                                        onClick={() => handlePrintImage(previewUrls.scene, 'scene')}
                                                                                        style={{
                                                                                            fontSize: '14px',
                                                                                            height: '32px',
                                                                                            borderRadius: '6px',
                                                                                            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                                                                                        }}
                                                                                    >
                                                                                        打印
                                                                                    </Button>
                                                                                    <Button
                                                                                        type="default"
                                                                                        icon={<DownloadOutlined />}
                                                                                        size="middle"
                                                                                        style={{
                                                                                            fontSize: '14px',
                                                                                            height: '32px',
                                                                                            borderRadius: '6px',
                                                                                            borderColor: '#52c41a',
                                                                                            color: '#52c41a',
                                                                                            boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)'
                                                                                        }}
                                                                                        onClick={async () => {
                                                                                            try {
                                                                                                const resp = await accidentService.downloadSceneImage(selectedAccident.id, 'scene');
                                                                                                const contentType = resp.headers['content-type'] || 'image/jpeg';
                                                                                                const disposition = resp.headers['content-disposition'] || '';
                                                                                                let filename = `实景记录图-${selectedAccident.accident_id}.jpg`;
                                                                                                try {
                                                                                                    const matchStar = disposition.match(/filename\*=(?:UTF-8''|)([^;\n]+)/i);
                                                                                                    const matchBasic = disposition.match(/filename="?([^";\n]+)"?/i);
                                                                                                    if (matchStar && matchStar[1]) {
                                                                                                        filename = decodeURIComponent(matchStar[1]);
                                                                                                    } else if (matchBasic && matchBasic[1]) {
                                                                                                        filename = matchBasic[1];
                                                                                                    }
                                                                                                } catch { }
                                                                                                const blob = new Blob([resp.data], { type: contentType });
                                                                                                const url = window.URL.createObjectURL(blob);
                                                                                                const link = document.createElement('a');
                                                                                                link.href = url;
                                                                                                link.download = filename;
                                                                                                document.body.appendChild(link);
                                                                                                link.click();
                                                                                                document.body.removeChild(link);
                                                                                                window.URL.revokeObjectURL(url);
                                                                                            } catch (error) {
                                                                                                message.error('下载失败');
                                                                                            }
                                                                                        }} >
                                                                                        下载
                                                                                    </Button>
                                                                                </Space>
                                                                            ),
                                                                        }}
                                                                    />
                                                                </div>
                                                            ) : <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>暂无实景记录图</div>
                                                        },
                                                        {
                                                            key: 'overhead',
                                                            label: '实景俯瞰记录图',
                                                            children: previewUrls.overhead ? (
                                                                <div style={{ textAlign: 'center' }}>
                                                                    <Image
                                                                        src={previewUrls.overhead}
                                                                        style={{ maxWidth: '100%' }}
                                                                        placeholder={true}
                                                                        preview={{
                                                                            src: previewUrls.overhead,
                                                                            toolbarRender: (
                                                                                _,
                                                                                {
                                                                                    transform: { scale },
                                                                                    actions: { onFlipY, onFlipX, onRotateLeft, onRotateRight, onZoomOut, onZoomIn, onReset },
                                                                                }
                                                                            ) => (
                                                                                <Space size={12} className="toolbar-wrapper">
                                                                                    <Button
                                                                                        type="primary"
                                                                                        icon={<PrinterOutlined />}
                                                                                        size="middle"
                                                                                        onClick={() => handlePrintImage(previewUrls.overhead, 'overhead')}
                                                                                        style={{
                                                                                            fontSize: '14px',
                                                                                            height: '32px',
                                                                                            borderRadius: '6px',
                                                                                            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                                                                                        }}
                                                                                    >
                                                                                        打印
                                                                                    </Button>
                                                                                    <Button
                                                                                        type="default"
                                                                                        icon={<DownloadOutlined />}
                                                                                        size="middle"
                                                                                        style={{
                                                                                            fontSize: '14px',
                                                                                            height: '32px',
                                                                                            borderRadius: '6px',
                                                                                            borderColor: '#52c41a',
                                                                                            color: '#52c41a',
                                                                                            boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)'
                                                                                        }}
                                                                                        onClick={async () => {
                                                                                            try {
                                                                                                const resp = await accidentService.downloadSceneImage(selectedAccident.id, 'overhead');
                                                                                                const contentType = resp.headers['content-type'] || 'image/jpeg';
                                                                                                const disposition = resp.headers['content-disposition'] || '';
                                                                                                let filename = `实景俯瞰记录图-${selectedAccident.accident_id}.jpg`;
                                                                                                try {
                                                                                                    const matchStar = disposition.match(/filename\*=(?:UTF-8''|)([^;\n]+)/i);
                                                                                                    const matchBasic = disposition.match(/filename="?([^";\n]+)"?/i);
                                                                                                    if (matchStar && matchStar[1]) {
                                                                                                        filename = decodeURIComponent(matchStar[1]);
                                                                                                    } else if (matchBasic && matchBasic[1]) {
                                                                                                        filename = matchBasic[1];
                                                                                                    }
                                                                                                } catch { }
                                                                                                const blob = new Blob([resp.data], { type: contentType });
                                                                                                const url = window.URL.createObjectURL(blob);
                                                                                                const link = document.createElement('a');
                                                                                                link.href = url;
                                                                                                link.download = filename;
                                                                                                document.body.appendChild(link);
                                                                                                link.click();
                                                                                                document.body.removeChild(link);
                                                                                                window.URL.revokeObjectURL(url);
                                                                                            } catch (error) {
                                                                                                message.error('下载失败');
                                                                                            }
                                                                                        }} >
                                                                                        下载
                                                                                    </Button>
                                                                                </Space>
                                                                            ),
                                                                        }}
                                                                    />
                                                                </div>
                                                            ) : <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>暂无实景俯瞰记录图</div>
                                                        }
                                                    ]}
                                                />
                                            </div>
                                        ) : (
                                            <div style={{
                                                border: '1px dashed #d9d9d9',
                                                borderRadius: 4,
                                                padding: '40px 16px',
                                                background: '#fafafa',
                                                textAlign: 'center'
                                            }}>
                                                <div style={{ marginBottom: 16, color: '#999', fontSize: 14 }}>
                                                    暂无现场图，请点击下方按钮绘制
                                                </div>
                                                <Button
                                                    type="primary"
                                                    icon={<PictureOutlined />}
                                                    onClick={handleOpenDrawing}
                                                    size="large"
                                                >
                                                    绘制现场图
                                                </Button>
                                            </div>
                                        )
                                    )}
                                </Col>
                            </Row>
                        </>
                    )}
                </Spin>
            </Drawer>

            {/* 道路交通事故现场绘图 Modal */}
            <Modal
                title={null}
                closable={false}
                open={showDrawing}
                onCancel={() => handleCloseDrawing()}
                afterOpenChange={handleModalAfterOpenChange}
                width="100vw"
                centered={false}
                style={{
                    top: 0,
                    left: 0,
                    maxWidth: '100vw',
                    paddingBottom: 0,
                    margin: 0,
                    padding: 0
                }}
                styles={{
                    body: {
                        padding: 0,
                        margin: 0,
                        overflow: 'hidden'
                    },
                    content: {
                        padding: 0,
                        margin: 0,
                        borderRadius: 0
                    }
                }}
                footer={null}
                wrapClassName="fullscreen-modal"
            >
                {/* 隐藏的文件选择器 - 保留给 WASM 调用 */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileInputChange}
                />
                {/* 加载提示
                {isLoading && (
                    <div style={{
                        position: 'absolute',
                        top: '33.33%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1000,
                        background: 'rgba(255, 255, 255, 0.95)',
                        padding: '20px 40px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        fontSize: '16px',
                        color: '#1890ff',
                        fontWeight: 500
                    }}>
                        {loadingText}
                    </div>
                )} */}

                {/* iframe 容器 */}
                <div
                    ref={iframeContainerRef}
                    style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        margin: 0,
                        padding: 0,
                        zIndex: 1,
                        background: '#fff',
                        overflow: 'hidden'
                    }}
                />
            </Modal>
        </div>
    );
};

export default AccidentQuery;
